import React from "react";
import { GripVertical } from "lucide-react";
import { useFloatingPanelDrag } from "./FloatingPanel";

/**
 * A dock panel's title bar: optional leading control (a back arrow), a title
 * area, and trailing actions.
 *
 * Doubles as the panel's move handle — the conventional grab area for a
 * floating window, and the one strip guaranteed not to be scrolling content.
 * The gesture comes from `FloatingPanel` through context, so this stays usable
 * on its own (outside a panel it simply doesn't drag) and callers never have to
 * thread drag props through.
 *
 * Presses that land on a button inside the header are excluded from the drag by
 * `useFloatingBox`'s interactive-target guard, so Close and Expand still work.
 *
 * Kept as slots rather than `title`/`subtitle` strings so a caller can put an
 * avatar, a link or a status chip in the title area without this component
 * growing a prop per case.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.leading]
 * @param {React.ReactNode} props.children - The title area.
 * @param {React.ReactNode} [props.actions]
 * @param {React.ReactNode} [props.below] - Second row, e.g. a segmented control.
 */
export function PanelHeader({ leading, children, actions, below }) {
  const { dragHandleProps, isMoving, canMove } = useFloatingPanelDrag();

  return (
    <header className="flex-shrink-0 border-b border-gray-100 bg-white">
      <div
        {...dragHandleProps}
        // `select-none` so a drag across the title never leaves text selected,
        // and `touch-none` so a touch drag isn't stolen by the browser's pan.
        className={`flex items-center gap-2 px-2.5 py-2.5 ${
          canMove
            ? `select-none touch-none ${
                isMoving ? "cursor-grabbing" : "cursor-grab"
              }`
            : ""
        }`}
      >
        {/* Says the panel moves. Without it the grab cursor is the only clue,
            and a cursor change is not something people go hunting for. */}
        {canMove && (
          <GripVertical
            aria-hidden="true"
            className="-ml-1 h-4 w-4 flex-shrink-0 text-gray-300"
          />
        )}
        {leading}
        <div className="min-w-0 flex-1">{children}</div>
        {actions && (
          <div className="flex flex-shrink-0 items-center gap-0.5">{actions}</div>
        )}
      </div>
      {below && <div className="px-2.5 pb-2.5">{below}</div>}
    </header>
  );
}

export default PanelHeader;
